import { Cents, CurrencyEnum, PaymentMethodEnum } from '@systemeio/ui'
import {
  BillingPeriodEnum,
  SubscriptionPeriodEnum,
} from 'modules/profile/user-plan/enums/billing-period-enum'
import { UserPlanTypeEnum } from 'modules/profile/user-plan/enums/user-plan-type-enum'

type UpgradePlanPaymentMethodType =
  | PaymentMethodEnum.stripe_applepay
  | PaymentMethodEnum.stripe_bancontact
  | PaymentMethodEnum.stripe_card
  | PaymentMethodEnum.stripe_ideal
  | PaymentMethodEnum.paypal_soap
  | PaymentMethodEnum.dlocal_boleto
  | PaymentMethodEnum.dlocal_card
  | PaymentMethodEnum.dlocal_pix

export interface UseUserPlanInterface {
  customer: {
    paymentMethod: UpgradePlanPaymentMethodType | null
  }
  userPlan: UserPlanInterface | null
  userPlans: UserPlanInterface[]
  userResources: UserResourcesInterface
  nextBillingDate: string | null
  subscriptionPeriod: SubscriptionPeriodEnum | null
}

export interface UserPlanInterface {
  applicationFee: number
  createdAt: string
  id: number
  maxAutomationRules: number
  maxBlogs: number
  maxCampaigns: number
  maxCommunities: number
  maxContacts: number
  maxCoupons: number
  maxDigitalStores: number
  maxDigitalStoreProducts: number
  maxFunnelSteps: number
  maxFunnels: number
  maxMembershipCourses: number
  maxOrderBumps: number
  maxSplitTests: number
  maxTags: number
  maxUpsells: number
  maxUserDomains: number
  maxWebinars: number
  maxWorkflows: number
  maxCrmPipelines: number
  maxBookingEvents: number
  maxStudents: number
  title: string
  type: UserPlanTypeEnum
  annualCost: Cents
  annualCostWithTax: Cents
  monthCost: Cents
  monthCostWithTax: Cents
  maxProductVariants: number
  currency: CurrencyEnum | null
  offerLinkAnnual: string
  offerLinkMonthly: string
}

export interface UserResourcesInterface {
  automationRules: number
  blogs: number
  campaigns: number
  communities: number
  contacts: number
  coupons: number
  courses: number
  digitalStoreProducts: number
  digitalStores: number
  funnelSteps: number
  funnels: number
  orderBumps: number
  splitTests: number
  tags: number
  upsells: number
  userDomains: number
  webinars: number
  workflows: number
  bookingEvents: number
  crmPipelines: number
  productVariants: number
  students: number
}

export interface UserPlanColumnsInterface {
  key: keyof Omit<
    UserPlanInterface,
    'createdAt' | 'title' | 'type' | 'currency' | 'offerLinkAnnual' | 'offerLinkMonthly'
  >
  title: string
  resourceKey?: keyof UserResourcesInterface
}

export const tableFeatures: UserPlanColumnsInterface[] = [
  {
    key: 'maxContacts',
    title: 'dashboard.settings.user_plan.contacts_title',
    resourceKey: 'contacts',
  },
  {
    key: 'maxFunnels',
    title: 'dashboard.settings.user_plan.sales_funnels_title',
    resourceKey: 'funnels',
  },
  {
    key: 'maxFunnelSteps',
    title: 'dashboard.settings.user_plan.sales_funnels_steps_title',
    resourceKey: 'funnelSteps',
  },
  { key: 'maxBlogs', title: 'dashboard.settings.user_plan.blogs_title', resourceKey: 'blogs' },
  {
    key: 'maxMembershipCourses',
    title: 'dashboard.settings.user_plan.membership_sites_title',
    resourceKey: 'courses',
  },
  {
    key: 'maxDigitalStores',
    title: 'dashboard.settings.user_plan.digital_stores_title',
    resourceKey: 'digitalStores',
  },
  {
    key: 'maxDigitalStoreProducts',
    title: 'dashboard.settings.user_plan.digital_store_products_title',
    resourceKey: 'digitalStoreProducts',
  },
  {
    key: 'maxAutomationRules',
    title: 'dashboard.settings.user_plan.automation_rules_title',
    resourceKey: 'automationRules',
  },
  {
    key: 'maxWorkflows',
    title: 'dashboard.settings.user_plan.workflows_title',
    resourceKey: 'workflows',
  },
  { key: 'maxTags', title: 'dashboard.settings.user_plan.tags_title', resourceKey: 'tags' },
  {
    key: 'maxCampaigns',
    title: 'dashboard.settings.user_plan.email_campaigns_title',
    resourceKey: 'campaigns',
  },
  {
    key: 'maxUpsells',
    title: 'dashboard.settings.user_plan.upsells_title',
    resourceKey: 'upsells',
  },
  {
    key: 'maxOrderBumps',
    title: 'dashboard.settings.user_plan.order_bumps_title',
    resourceKey: 'orderBumps',
  },
  {
    key: 'maxSplitTests',
    title: 'dashboard.settings.user_plan.a_b_tests_title',
    resourceKey: 'splitTests',
  },
  {
    key: 'maxCoupons',
    title: 'dashboard.settings.user_plan.coupon_codes_title',
    resourceKey: 'coupons',
  },
  {
    key: 'maxUserDomains',
    title: 'dashboard.settings.user_plan.custom_domains_title',
    resourceKey: 'userDomains',
  },
  {
    key: 'maxWebinars',
    title: 'dashboard.settings.user_plan.webinars_title',
    resourceKey: 'webinars',
  },
  {
    key: 'maxCommunities',
    title: 'dashboard.settings.user_plan.communities_title',
    resourceKey: 'communities',
  },
  {
    key: 'maxProductVariants',
    title: 'dashboard.settings.user_plan.max_product_variants_title',
    resourceKey: 'productVariants',
  },
  {
    key: 'maxCrmPipelines',
    title: 'dashboard.settings.user_plan.max_pipeline_title',
    resourceKey: 'crmPipelines',
  },
  {
    key: 'maxBookingEvents',
    title: 'dashboard.settings.user_plan.max_booking_events_title',
    resourceKey: 'bookingEvents',
  },
  {
    key: 'maxStudents',
    title: 'dashboard.settings.user_plan.max_students_title',
    resourceKey: 'students',
  },
]

export interface UpgradeUserPlanInterface {
  userPlan: number
  period: BillingPeriodEnum
}

export interface UpgradeUserPlanResponseInterface {
  redirectUrl?: string
  intentClientSecret?: string
}
